import React, { useEffect, useState, useRef, useMemo, Suspense, lazy } from 'react';
import { auth, db, storage } from './firebase/config';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import {
  ref,
  deleteObject,
  getBlob,
} from 'firebase/storage';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { runAzureOcr, runGptExtract } from './firebase/gptExtract';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import CameraScanner from './components/CameraScanner';
import ExtractionStepper from './components/ExtractionStepper';
import { convertImagesToPdf } from './utils/convertImagesToPdf';
import {
  cleanupTemp,
  isDuplicate,
  prepareForExtraction,
  saveInvoiceToFirestore,
} from './utils/invoiceUpload';
import {
  applyVatMappingSync,
  loadVatMappingsMap,
} from './utils/vatMapping';
import { el, formatInvoiceFieldLabel } from './i18n/el';

const BulkUploadDialog = lazy(() => import('./components/BulkUploadDialog'));

// Date picker component that integrates flatpickr with DD/MM/YYYY format
function IssueDatePicker({ value, onChange, inputClassName }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!inputRef.current) return;
    const fp = flatpickr(inputRef.current, {
      dateFormat: 'd/m/Y',
      defaultDate: value ? (value instanceof Date ? value : new Date(value)) : null,
      onChange: (dates) => {
        const d = dates && dates[0] ? dates[0] : null;
        onChange && onChange(d);
      }
    });
    return () => fp && fp.destroy();
  }, [value, onChange]);

  const dv = value instanceof Date
    ? `${String(value.getDate()).padStart(2,'0')}/${String(value.getMonth()+1).padStart(2,'0')}/${value.getFullYear()}`
    : '';

  return (
    <input
      ref={inputRef}
      type="text"
      className={inputClassName || "w-full border rounded px-3 py-2"}
      placeholder={el.datePlaceholder}
      defaultValue={dv}
      readOnly
    />
  );
}

function SortableHeader({ label, sortMode, setSortMode, ascValue, descValue, align = 'left' }) {
  const isActive = sortMode === ascValue || sortMode === descValue;
  const isAsc = sortMode === ascValue;
  const next = isActive ? (isAsc ? descValue : ascValue) : descValue;
  const alignClass = align === 'right' ? 'justify-end text-right' : 'text-left';
  return (
    <th className={`px-4 py-3 font-medium uppercase tracking-wide text-xs ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => setSortMode(next)}
        className={`inline-flex items-center gap-1 group ${alignClass} ${isActive ? 'text-blue-700' : 'text-gray-600 hover:text-gray-800'}`}
        aria-label={isAsc ? el.dashSortAriaAsc : el.dashSortAriaDesc}
      >
        <span>{label}</span>
        <span className={`flex flex-col leading-none transition ${isActive ? 'opacity-100' : 'opacity-30 group-hover:opacity-60'}`}>
          <svg className={`h-2.5 w-2.5 ${isActive && isAsc ? 'text-blue-700' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z"/></svg>
          <svg className={`h-2.5 w-2.5 -mt-0.5 ${isActive && !isAsc ? 'text-blue-700' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z"/></svg>
        </span>
      </button>
    </th>
  );
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export default function Dashboard() {
  const [invoices, setInvoices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [formData, setFormData] = useState({});
  const [filePreview, setFilePreview] = useState(null);
  const [finalFile, setFinalFile] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [deleteInvoiceDialog, setDeleteInvoiceDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [globalDateRange, setGlobalDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return [startOfDay(start), endOfDay(end)];
  });
  const [sortMode, setSortMode] = useState('date_desc');
  const [datePreset, setDatePreset] = useState('30d');
  const [filterText, setFilterText] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [editInvoice, setEditInvoice] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [displayLimit, setDisplayLimit] = useState(20);

  const fileInputRef = useRef(null);
  const mobileCameraRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [tempFilePath, setTempFilePath] = useState(null);
  const [extractionUrl, setExtractionUrl] = useState(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState({ active: false, done: 0, total: 0, error: false });

  // Two-step AI scan progress within the review dialog
  const [ocrStep, setOcrStep] = useState('idle'); // idle | running | done | error
  const [gptStep, setGptStep] = useState('idle');
  const [scanError, setScanError] = useState('');
  const [aiScanning, setAiScanning] = useState(false);
  const [pendingHash, setPendingHash] = useState(null);
  const vatMappingsRef = useRef(new Map());

  const user = auth.currentUser;

  useEffect(() => {
    if (!companyId) return;
    loadVatMappingsMap(companyId).then((map) => {
      vatMappingsRef.current = map;
    });
  }, [companyId]);

  const updateReviewField = (key, value) => {
    if (key === 'issuer_vat') {
      const next = applyVatMappingSync({ ...formData, issuer_vat: value }, vatMappingsRef.current);
      setFormData(next);
      return;
    }
    setFormData({ ...formData, [key]: value, ...(key === 'issuer_company_name' ? { _nameFromMapping: false } : {}) });
  };

  // First useEffect to determine user role and company ID
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    // First, check if this user is a company owner
    getDoc(doc(db, 'companies', user.uid))
      .then(docSnap => {
        if (docSnap.exists()) {
          // This user is a company owner
          const data = docSnap.data();
          setCompanyName(data.companyName);
          setCompanyId(user.uid);
          setUserRole('owner');
          return true; // User is an owner
        } else {
          // This user might be a company member, check memberships
          return getDoc(doc(db, 'userMemberships', user.uid))
            .then(membershipSnap => {
              if (membershipSnap.exists()) {
                const membershipData = membershipSnap.data();
                setCompanyId(membershipData.companyId);
                setUserRole(membershipData.role);
                
                // Get company name
                return getDoc(doc(db, 'companies', membershipData.companyId))
                  .then(companySnap => {
                    if (companySnap.exists()) {
                      setCompanyName(companySnap.data().companyName);
                    }
                    // Prefer authoritative role from the company members doc when available
                    return getDoc(doc(db, 'companies', membershipData.companyId, 'members', user.uid)).then(memberSnap => {
                      if (memberSnap.exists()) {
                        const role = memberSnap.data()?.role;
                        if (role) setUserRole(role);
                      }
                      return true; // User is a member
                    });
                  });
              }
              return false; // User doesn't have any membership
            });
        }
      })
      .catch(error => {
        console.error("Error determining user role:", error);
        showToast('error', el.toastFailedLoadUser);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user]);

  // Reload invoices when company changes (filters & sort are applied client-side
  // via the `visibleInvoices` memo, so they don't require a refetch).
  useEffect(() => {
    if (companyId) {
      setDisplayLimit(20);
      loadInvoices();
    }
  }, [companyId]);

  const loadInvoices = async () => {
    if (!companyId) return;
    try {
      const q = query(
        collection(db, 'companies', companyId, 'invoices'),
        orderBy('issue_date', 'desc'),
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => {
        const v = d.data();
        return {
          id: d.id,
          ...v,
          issue_date: v.issue_date?.seconds
            ? new Date(v.issue_date.seconds * 1000)
            : new Date(v.issue_date),
          uploadedAt: v.uploadedAt?.seconds
            ? new Date(v.uploadedAt.seconds * 1000)
            : (v.uploadedAt ? new Date(v.uploadedAt) : null),
        };
      });
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      showToast('error', el.toastFailedLoadInvoices);
    }
  };

  // Quick date-range presets used by the filter chip row.
  const DATE_PRESETS = useMemo(() => [
    { id: '30d', label: el.dashPreset30d, days: 30 },
    { id: 'today', label: el.dashPresetToday, days: 0 },
    { id: '7d', label: el.dashPreset7d, days: 7 },
    { id: 'month', label: el.dashPresetMonth, days: null, kind: 'month' },
    { id: 'year', label: el.dashPresetYear, days: null, kind: 'year' },
    { id: 'all', label: el.dashPresetAll, days: null, kind: 'all' },
  ], []);

  const applyDatePreset = (presetId) => {
    setDatePreset(presetId);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    let start;
    const preset = DATE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    if (preset.kind === 'all') {
      start = new Date(2000, 0, 1);
    } else if (preset.kind === 'month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (preset.kind === 'year') {
      start = new Date(end.getFullYear(), 0, 1);
    } else if (preset.days === 0) {
      start = new Date(); start.setHours(0, 0, 0, 0);
    } else {
      start = new Date();
      start.setDate(end.getDate() - (preset.days - 1));
      start.setHours(0, 0, 0, 0);
    }
    setGlobalDateRange([start, end]);
  };

  const clearFilters = () => {
    setFilterText('');
    applyDatePreset('30d');
    setSortMode('date_desc');
  };

  // One-shot filter+sort pipeline. Recomputes only when its inputs change so
  // selecting a row or opening a dialog doesn't re-sort the entire list.
  const visibleInvoices = useMemo(() => {
    const search = filterText.trim().toLowerCase();
      const [rangeStart, rawRangeEnd] = globalDateRange;
      const rangeEnd = endOfDay(rawRangeEnd);
    const filtered = invoices.filter((inv) => {
      const d = inv.issue_date instanceof Date ? inv.issue_date : new Date(inv.issue_date);
      if (d < rangeStart || d > rangeEnd) return false;
      if (!search) return true;
      return (
        (inv.extracted?.invoice_number || '').toLowerCase().includes(search) ||
        (inv.extracted?.issuer_company_name || '').toLowerCase().includes(search) ||
        (inv.extracted?.issuer_vat || '').toLowerCase().includes(search)
      );
    });
    const amountOf = (inv) => {
      const v = parseFloat(String(inv.extracted?.grand_total || '').replace(/[^0-9.-]/g, ''));
      return Number.isNaN(v) ? 0 : v;
    };
    const supplierOf = (inv) => (inv.extracted?.issuer_company_name || '').toLocaleLowerCase('el');
    const sorted = [...filtered].sort((a, b) => {
      switch (sortMode) {
        case 'date_asc':
          return new Date(a.issue_date) - new Date(b.issue_date);
        case 'amount_desc':
          return amountOf(b) - amountOf(a);
        case 'amount_asc':
          return amountOf(a) - amountOf(b);
        case 'supplier_asc':
          return supplierOf(a).localeCompare(supplierOf(b), 'el');
        case 'supplier_desc':
          return supplierOf(b).localeCompare(supplierOf(a), 'el');
        case 'date_desc':
        default:
          return new Date(b.issue_date) - new Date(a.issue_date);
      }
    });
    return sorted;
  }, [invoices, globalDateRange, filterText, sortMode]);

  const hasActiveFilters =
    !!filterText.trim() || sortMode !== 'date_desc' || datePreset !== '30d';

  const EMPTY_INVOICE_FIELDS = {
    issue_date: '',
    invoice_number: '',
    grand_total: '',
    issuer_company_name: '',
    issuer_vat: '',
  };

  const processFile = async (file) => {
    if (!file || !user || !companyId) return;
    if (!(userRole === 'owner' || userRole === 'manager')) {
      showToast('error', el.toastNoUploadPermission);
      return;
    }
    // 15 MB is enough headroom for multi-page scanned PDFs while staying well
    // below Azure's paid-tier file limit and Firebase Storage best practice.
    if (file.size > 15 * 1024 * 1024) {
      showToast('warning', el.toastFileTooLarge);
      return;
    }

    setUploading(true);
    try {
      const prepared = await prepareForExtraction(file, companyId);
      if (await isDuplicate(companyId, prepared.fileHash)) {
        await cleanupTemp(prepared.tempPath);
        showToast('warning', el.toastDuplicateFile);
        setUploading(false);
        return;
      }

      const filePreviewUrl = URL.createObjectURL(prepared.finalFile);
      setFilePreview(filePreviewUrl);
      setPreviewObjectUrl(filePreviewUrl);
      setFinalFile(prepared.finalFile);
      setTempFilePath(prepared.tempPath);
      setExtractionUrl(prepared.extractionUrl);
      setPendingHash(prepared.fileHash);
      setExtractedData(EMPTY_INVOICE_FIELDS);
      setFormData(EMPTY_INVOICE_FIELDS);
      setOcrStep('idle');
      setGptStep('idle');
      setScanError('');
      setDialogVisible(true);
    } catch (err) {
      console.error('Upload failed:', err);
      showToast('error', el.toastUploadFailed);
    } finally {
      setUploading(false);
    }
  };

  const runAiScan = async () => {
    if (!extractionUrl || aiScanning) return;
    setAiScanning(true);
    setScanError('');
    setOcrStep('running');
    setGptStep('idle');
    try {
      const ocr = await runAzureOcr(extractionUrl);
      if (ocr.error) {
        setOcrStep('error');
        setScanError(ocr.error);
        return;
      }
      setOcrStep('done');
      setGptStep('running');
      const fields = await runGptExtract(ocr.ocrText);
      if (fields.error) {
        setGptStep('error');
        setScanError(fields.error);
        return;
      }
      setGptStep('done');
      const map = await loadVatMappingsMap(companyId);
      vatMappingsRef.current = map;
      const merged = applyVatMappingSync({ ...EMPTY_INVOICE_FIELDS, ...fields }, map);
      setExtractedData(merged);
      setFormData(merged);
    } catch (e) {
      console.error('AI scan failed', e);
      setScanError(e?.message || el.dashAiError);
      if (ocrStep !== 'done') setOcrStep('error');
      else setGptStep('error');
    } finally {
      setAiScanning(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = null;
    if (!file) return;
    await processFile(file);
  };

  const handleMobileCameraCapture = async (e) => {
    const file = e.target.files[0];
    if (mobileCameraRef.current) mobileCameraRef.current.value = null;
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) await processFile(file);
  };

  // Always open the in-app multi-page scanner. The native camera input only
  // captures one image at a time, which prevented multi-page invoices on mobile.
  // On rare devices where getUserMedia is unavailable we fall back to the native
  // camera picker (single page).
  const handleCameraAction = () => {
    const hasUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    if (hasUserMedia && window.isSecureContext) {
      setCameraOpen(true);
    } else {
      mobileCameraRef.current?.click();
    }
  };

  const cleanupTempUploads = async () => {
    try {
      if (tempFilePath) {
        await cleanupTemp(tempFilePath);
      }
    } finally {
      setTempFilePath(null);
      setExtractionUrl(null);
      setPendingHash(null);
    }
  };

  const resetReviewState = () => {
    setExtractedData(null);
    setFormData({});
    setFilePreview(null);
    setFinalFile(null);
    setOcrStep('idle');
    setGptStep('idle');
    setScanError('');
    setAiScanning(false);
  };

  const handleConfirmSave = async () => {
    if (!canManageInvoices) {
      showToast('error', el.toastNoSavePermission);
      return;
    }
    if (!companyId) {
      showToast('error', el.toastNoCompany);
      return;
    }
    if (!finalFile) {
      showToast('error', el.toastSaveFailed);
      return;
    }

    try {
      await saveInvoiceToFirestore({
        companyId,
        user,
        finalFile,
        fields: formData,
        fileHash: pendingHash,
      });
      showToast('success', el.toastInvoiceSaved);
      setDialogVisible(false);
      await cleanupTempUploads();
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
        setPreviewObjectUrl(null);
      }
      resetReviewState();
      await loadInvoices();
    } catch (error) {
      console.error('Save failed:', error);
      showToast('error', el.toastSaveFailed);
    }
  };

  const handleCancelReview = () => {
    setCancelConfirmVisible(true);
  };

  const confirmCancelReview = async () => {
    try {
      await cleanupTempUploads();
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
        setPreviewObjectUrl(null);
      }
    } finally {
      setDialogVisible(false);
      setCancelConfirmVisible(false);
      resetReviewState();
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditInvoice(invoice);
    setEditFormData({
      invoice_number: invoice.extracted?.invoice_number || '',
      issue_date: invoice.issue_date || '',
      grand_total: invoice.extracted?.grand_total || '',
      issuer_company_name: invoice.extracted?.issuer_company_name || '',
      issuer_vat: invoice.extracted?.issuer_vat || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editInvoice || !companyId) return;
    try {
      const parsed = (() => {
        const v = editFormData.issue_date;
        if (!v) return null;
        if (v instanceof Date) return v;
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) return d;
        const m = /^([0-3]?\d)[\/\-]([0-1]?\d)[\/\-](\d{4})$/.exec(String(v));
        if (m) {
          const dd = new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10));
          if (!Number.isNaN(dd.getTime())) return dd;
        }
        return null;
      })();
      await updateDoc(doc(db, 'companies', companyId, 'invoices', editInvoice.id), {
        extracted: {
          ...(editInvoice.extracted || {}),
          invoice_number: editFormData.invoice_number,
          grand_total: editFormData.grand_total,
          issuer_company_name: editFormData.issuer_company_name,
          issuer_vat: editFormData.issuer_vat,
        },
        ...(parsed ? { issue_date: parsed } : {}),
      });
      showToast('success', el.toastInvoiceUpdated);
      setEditInvoice(null);
      setEditFormData({});
      await loadInvoices();
    } catch (err) {
      console.error('Edit save failed:', err);
      showToast('error', el.toastUpdateFailed);
    }
  };

  const confirmDeleteInvoice = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteInvoiceDialog(true);
  };

  const deleteInvoice = async () => {
    if (!canManageInvoices) {
      showToast('error', el.toastNoDeletePermission);
      return;
    }
    if (!companyId || !invoiceToDelete) {
      showToast('error', el.toastNoInvoiceSelected);
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'invoices', invoiceToDelete.id));
      const fileRef = ref(storage, `invoices/${companyId}/${invoiceToDelete.fileName}`);
      await deleteObject(fileRef);
      showToast('info', el.toastInvoiceDeleted);
      setDeleteInvoiceDialog(false);
      setInvoiceToDelete(null);
      loadInvoices();
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('error', el.toastDeleteFailed);
    }
  };

  const deleteSelectedInvoices = async () => {
    if (!canManageInvoices) {
      showToast('error', el.toastNoDeletePermission);
    return;
    }
    if (!companyId || !selectedInvoices || !selectedInvoices.length) return;
    
    try {
      for (const invoice of selectedInvoices) {
        await deleteDoc(doc(db, 'companies', companyId, 'invoices', invoice.id));
        const fileRef = ref(storage, `invoices/${companyId}/${invoice.fileName}`);
        await deleteObject(fileRef);
      }
      showToast('success', el.toastBulkDeleted);
      setSelectedInvoices([]);
      loadInvoices();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      showToast('error', el.toastBulkDeleteFailed);
    }
  };

  const downloadSelectedInvoices = async () => {
    if (!selectedInvoices || !selectedInvoices.length) return;

    const total = selectedInvoices.length;
    setDownloadStatus({ active: true, done: 0, total, error: false });

    try {
      const zip = new JSZip();

      // CSV with BOM for correct UTF-8 in Excel
      const csvHeader = 'Αρχείο,Ημερομηνία έκδοσης,Εκδότης,ΑΦΜ εκδότη,Σύνολο\n';
      const csvRows = selectedInvoices.map(inv => {
        const date = inv.issue_date
          ? (inv.issue_date instanceof Date ? inv.issue_date : new Date(inv.issue_date)).toLocaleDateString('el-GR')
          : '';
        const issuer = (inv.extracted?.issuer_company_name || '').replace(/"/g, '""');
        const vat = inv.extracted?.issuer_vat || '';
        const total2 = inv.extracted?.grand_total || '';
        const fileName = inv.fileName || '';
        return `"${fileName}","${date}","${issuer}","${vat}","${total2}"`;
      }).join('\n');
      zip.file('τιμολόγια.csv', '\uFEFF' + csvHeader + csvRows);

      // Download PDFs sequentially, updating counter per file
      for (let i = 0; i < selectedInvoices.length; i++) {
        const invoice = selectedInvoices[i];
        const fileRef = ref(storage, `invoices/${companyId}/${invoice.fileName}`);
        const blob = await getBlob(fileRef);
        zip.file(invoice.fileName, blob);
        setDownloadStatus({ active: true, done: i + 1, total, error: false });
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const date = new Date().toISOString().split('T')[0];
      saveAs(content, `${companyName || 'Invoices'}_${date}.zip`);
      // Show "done" briefly then hide
      setDownloadStatus({ active: true, done: total, total, error: false, finished: true });
      setTimeout(() => setDownloadStatus({ active: false, done: 0, total: 0, error: false }), 2500);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadStatus({ active: true, done: 0, total, error: true });
      setTimeout(() => setDownloadStatus({ active: false, done: 0, total: 0, error: false }), 3000);
      showToast('error', el.toastDownloadFailed);
    }
  };

  const [toast, setToast] = useState({ visible: false, severity: '', message: '' });
  const canManageInvoices = userRole === 'owner' || userRole === 'manager';
  
  const showToast = (severity, message) => {
    setToast({ visible: true, severity, message });
    setTimeout(() => {
      setToast({ visible: false, severity: '', message: '' });
    }, 3000);
  };

  const toggleSelectInvoice = (invoice) => {
    const isSelected = selectedInvoices.some(inv => inv.id === invoice.id);
    if (isSelected) {
      setSelectedInvoices(selectedInvoices.filter(inv => inv.id !== invoice.id));
    } else {
      setSelectedInvoices([...selectedInvoices, invoice]);
    }
  };

  const toggleSelectAll = () => {
    // Select all currently visible (filtered+sorted) invoices, or clear the
    // selection if everything visible is already selected.
    if (selectedInvoices.length > 0 && selectedInvoices.length === visibleInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices([...visibleInvoices]);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatISODate = (date) => {
    if (!date) return '';
    let d;
    if (date instanceof Date) {
      d = date;
    } else {
      d = parseToDate ? parseToDate(date) : new Date(date);
      if (!d || Number.isNaN(d.getTime())) {
        d = new Date(date);
      }
    }
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const parseToDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const nat = new Date(value);
    if (!Number.isNaN(nat.getTime())) return nat;
    const m = /^([0-3]?\d)[\/\-]([0-1]?\d)[\/\-](\d{4})$/.exec(String(value));
    if (m) {
      const day = parseInt(m[1], 10);
      const month = parseInt(m[2], 10) - 1;
      const year = parseInt(m[3], 10);
      const d = new Date(year, month, day);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
  };

  const formatHumanDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('el-GR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    const asNumber = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(asNumber)) return `€ ${value}`;
    return `€ ${asNumber.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const stats = useMemo(() => {
    const totalAmount = visibleInvoices.reduce((sum, inv) => {
      const v = parseFloat(String(inv.extracted?.grand_total || '').replace(/[^0-9.-]/g, ''));
      return sum + (Number.isNaN(v) ? 0 : v);
    }, 0);
    const uniqueVats = new Set(
      visibleInvoices.map((inv) => (inv.extracted?.issuer_vat || '').trim()).filter(Boolean),
    );
    return { totalInvoices: visibleInvoices.length, totalAmount, uniqueVats: uniqueVats.size };
  }, [visibleInvoices]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">{el.dashLoading}</div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="p-4 max-w-full">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
          <p className="font-bold">{el.dashNoCompanyTitle}</p>
          <p>{el.dashNoCompanyBody}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 max-w-full">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {(() => {
          const { totalInvoices, totalAmount, uniqueVats } = stats;
          return (
            <>
              {/* Total Invoices */}
              <div className="relative overflow-hidden bg-white border rounded-2xl p-4 sm:p-5">
                <div className="absolute -top-3 -right-3 w-28 h-28 rounded-full bg-blue-50 z-0"></div>
                <div className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-blue-100 border border-blue-200 flex items-center justify-center z-10">
                  <svg className="h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                </div>
                <div className="text-sm text-gray-600">{el.dashStatInvoices}</div>
                <div className="text-2xl font-semibold mt-1">{totalInvoices}</div>
              </div>

              {/* Total Amount */}
              <div className="relative overflow-hidden bg-white border rounded-2xl p-4 sm:p-5">
                <div className="absolute -top-3 -right-3 w-28 h-28 rounded-full bg-green-50 z-0"></div>
                <div className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-green-100 border border-green-200 flex items-center justify-center z-10">
                  <svg className="h-4 w-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V4m0 16v-4"/></svg>
                </div>
                <div className="text-sm text-gray-600">{el.dashStatAmount}</div>
                <div className="text-2xl font-semibold mt-1">{formatCurrency(totalAmount)}</div>
              </div>

              {/* Unique Companies */}
              <div className="relative overflow-hidden bg-white border rounded-2xl p-4 sm:p-5">
                <div className="absolute -top-3 -right-3 w-28 h-28 rounded-full bg-orange-50 z-0"></div>
                <div className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-orange-100 border border-orange-200 flex items-center justify-center z-10">
                  <svg className="h-4 w-4 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7l9-4 9 4-9 4-9-4zm0 6l9 4 9-4"/></svg>
                </div>
                <div className="text-sm text-gray-600">{el.dashStatSuppliers}</div>
                <div className="text-2xl font-semibold mt-1">{uniqueVats}</div>
              </div>
            </>
          );
        })()}
      </div>
      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          toast.severity === 'success' ? 'bg-green-500' :
          toast.severity === 'error' ? 'bg-red-500' :
          toast.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
        } text-white`}>
          <div className="flex items-center">
            {toast.severity === 'success' && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {toast.severity === 'error' && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {toast.severity === 'warning' && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            {toast.severity === 'info' && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
      
      {/* Lightweight Upload spinner */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl px-6 py-5 flex items-center gap-3">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
            <span className="text-gray-800">{el.dashProcessing}</span>
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className={`bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 shadow-sm ${!canManageInvoices ? 'opacity-60 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center text-gray-700 font-semibold">
            <svg className="h-5 w-5 mr-2 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M8 12l4-4m0 0l4 4m-4-4v12" /></svg>
            {el.dashUploadTitle}
          </div>
          <button
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5V6a2 2 0 012-2h11l5 5v7.5a2 2 0 01-2 2H5a2 2 0 01-2-2zM12 11v6m-3-3h6"/></svg>
            {el.dashBulkOpen}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {/* File upload with drag & drop */}
          <label
            className={`group block w-full rounded-xl p-5 sm:p-8 text-center cursor-pointer transition-all border-2 border-dashed ${isDragOver ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/40'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center mb-3 transition-colors ${isDragOver ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-600'}`}>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
              </div>
              <div className="font-medium text-gray-800">{el.dashFromFile}</div>
              <div className="text-xs text-gray-500 mt-1">{el.dashFromFileHint}</div>
              <div className="text-xs text-gray-400 mt-0.5">{el.dashDropHint}</div>
              <div className="mt-4">
                <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors">{el.dashChooseFile}</span>
              </div>
            </div>
            <input hidden type="file" ref={fileInputRef} onChange={handleFileUpload} accept="application/pdf,image/*" disabled={!canManageInvoices} />
          </label>

          {/* Camera scan */}
          <div className="group block w-full rounded-xl p-5 sm:p-8 text-center border-2 border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50/40 transition-all">
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mb-3 group-hover:bg-purple-100 group-hover:text-purple-600 transition-colors">
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h4l2-3h6l2 3h4v11a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>
              </div>
              <div className="font-medium text-gray-800">{el.dashScanTitle}</div>
              <div className="text-xs text-gray-500 mt-1">{el.dashScanHint}</div>
              <div className="mt-4">
                <button onClick={handleCameraAction} className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg shadow-sm hover:bg-purple-700 transition-colors" disabled={!canManageInvoices}>
                  <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h4l2-3h6l2 3h4v11a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>
                  {el.dashOpenCamera}
                </button>
              </div>
            </div>
            <input hidden type="file" ref={mobileCameraRef} accept="image/*" capture="environment" onChange={handleMobileCameraCapture} />
          </div>
        </div>
      </div>
      
      {/* Filters & Search */}
      <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-5 mb-3 sm:mb-4 shadow-sm">
        {/* Search + Sort */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/></svg>
            <input
              type="search"
              inputMode="search"
              className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-9 pr-9 h-11 text-[15px] sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white focus:border-blue-300 transition"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={el.dashSearchPlaceholder}
              aria-label={el.dashSearchPlaceholder}
            />
            {filterText && (
              <button
                type="button"
                onClick={() => setFilterText('')}
                aria-label={el.dashSearchClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-200/60 hover:text-gray-700 flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            )}
          </div>
          <div className="relative w-full lg:w-auto">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h13M3 12h9M3 18h5M17 8v12m0 0l3-3m-3 3l-3-3"/></svg>
            <select
              className="w-full lg:w-auto appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-9 pr-9 h-11 text-[15px] sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white focus:border-blue-300"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              aria-label={el.dashSortLabel}
            >
              <option value="date_desc">{el.dashSortNewest}</option>
              <option value="date_asc">{el.dashSortOldest}</option>
              <option value="amount_desc">{el.dashSortAmountDesc}</option>
              <option value="amount_asc">{el.dashSortAmountAsc}</option>
              <option value="supplier_asc">{el.dashSortSupplierAsc}</option>
              <option value="supplier_desc">{el.dashSortSupplierDesc}</option>
            </select>
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z" clipRule="evenodd"/></svg>
          </div>
        </div>

        {/* Date preset chips */}
        <div className="mt-3 -mx-1 flex gap-1.5 overflow-x-auto pb-1 snap-x">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => applyDatePreset(p.id)}
              className={`shrink-0 snap-start px-3 h-8 rounded-full text-xs sm:text-sm font-medium border transition ${
                datePreset === p.id
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom date range (compact) */}
        <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex items-stretch gap-2 flex-1">
            <div className="flex items-center gap-2 flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 h-11">
              <svg className="h-4 w-4 text-gray-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M3 11h18M5 19h14a2 2 0 002-2v-6H3v6a2 2 0 002 2z"/></svg>
              <IssueDatePicker
                value={globalDateRange[0]}
                onChange={(d) => { setDatePreset(null); setGlobalDateRange([startOfDay(d || globalDateRange[0]), globalDateRange[1]]); }}
                inputClassName="flex-1 min-w-0 outline-none border-none text-[15px] sm:text-sm bg-transparent h-full"
              />
            </div>
            <div className="flex items-center gap-2 flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 h-11">
              <span className="text-gray-300 text-sm flex-shrink-0">→</span>
              <IssueDatePicker
                value={globalDateRange[1]}
                onChange={(d) => { setDatePreset(null); setGlobalDateRange([globalDateRange[0], endOfDay(d || globalDateRange[1])]); }}
                inputClassName="flex-1 min-w-0 outline-none border-none text-[15px] sm:text-sm bg-transparent h-full"
              />
            </div>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1.5 px-3 h-11 sm:h-11 rounded-lg text-sm text-gray-700 border border-gray-200 bg-white hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4l16 16M4 20L20 4"/></svg>
              {el.dashFiltersClear}
            </button>
          )}
        </div>
      </div>

      {/* Result count + sticky bulk-action bar */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-2 px-1">
        <span>{el.dashResultCount(visibleInvoices.length)}</span>
        {selectedInvoices.length > 0 && (
          <span className="text-blue-600 font-medium">{el.dashSelectedCount(selectedInvoices.length)}</span>
        )}
      </div>

      {selectedInvoices.length > 0 && (
        <div className="hidden lg:flex items-center justify-between mb-3 px-4 py-2.5 rounded-xl bg-blue-50 border border-blue-200">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-700">{el.dashSelectedCount(selectedInvoices.length)}</span>
            <button
              onClick={() => setSelectedInvoices([])}
              className="text-xs text-blue-700 hover:underline"
            >
              {el.dashClearSelection}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadSelectedInvoices}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-blue-200 text-blue-700 text-sm hover:bg-blue-100/40"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M8 12l4 4m0 0l4-4m-4 4V4"/></svg>
              {el.dashDownloadSelected}
            </button>
            {canManageInvoices && (
              <button
                onClick={deleteSelectedInvoices}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                {el.dashDeleteSelected}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Empty state — no invoices at all */}
      {invoices.length === 0 && (
        <div className="bg-white border border-dashed border-gray-200 rounded-2xl py-12 sm:py-16 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center mb-1">
              <svg className="h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            </div>
            <p className="text-gray-700 font-semibold text-base">{el.dashNoInvoices}</p>
            <p className="text-gray-500 text-sm max-w-md">{el.dashNoInvoicesHint}</p>
            {canManageInvoices && (
              <div className="flex gap-2 mt-3 flex-wrap justify-center">
                <button onClick={() => fileInputRef.current?.click()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">{el.dashChooseFile}</button>
                <button onClick={handleCameraAction} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700">{el.dashOpenCamera}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filtered empty state — invoices exist but none match filters */}
      {invoices.length > 0 && visibleInvoices.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl py-10 sm:py-14 text-center">
          <div className="flex flex-col items-center gap-2 px-4">
            <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center mb-1">
              <svg className="h-7 w-7 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/></svg>
            </div>
            <p className="text-gray-700 font-semibold">{el.dashNoFilterResults}</p>
            <p className="text-gray-500 text-sm max-w-md">{el.dashNoFilterResultsHint}</p>
            <button
              onClick={clearFilters}
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              {el.dashFiltersClear}
            </button>
          </div>
        </div>
      )}

      {/* Mobile card list (< lg) */}
      {visibleInvoices.length > 0 && (
        <div className="lg:hidden space-y-2.5">
          {visibleInvoices.slice(0, displayLimit).map((invoice) => {
            const isSelected = selectedInvoices.some(inv => inv.id === invoice.id);
            const uploaderName = invoice.uploadedBy?.name || invoice.uploadedBy?.email || '-';
            const initial = (invoice.uploadedBy?.name || invoice.uploadedBy?.email || '?')[0].toUpperCase();
            return (
              <div
                key={invoice.id}
                className={`relative bg-white border rounded-2xl p-3.5 shadow-sm transition-all ${
                  isSelected ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <label className="flex items-center pt-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectInvoice(invoice)}
                      className="w-5 h-5 rounded text-blue-600 focus:ring-blue-400"
                      aria-label={el.dashSelectInvoice || 'Επιλογή'}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => window.open(invoice.url, '_blank')}
                    className="flex-1 min-w-0 text-left"
                    aria-label={el.dashOpenInvoice}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate text-[15px]">
                          {invoice.extracted?.issuer_company_name || '—'}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {formatHumanDate(invoice.issue_date)}
                          {invoice.extracted?.invoice_number && (
                            <> · #{invoice.extracted.invoice_number}</>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-blue-700 font-bold tabular-nums whitespace-nowrap">
                          {formatCurrency(invoice.extracted?.grand_total)}
                        </div>
                        {invoice.extracted?.issuer_vat && (
                          <div className="text-[11px] text-gray-400 mt-0.5">ΑΦΜ {invoice.extracted.issuer_vat}</div>
                        )}
                      </div>
                    </div>
                  </button>
                </div>

                <div className="flex items-center justify-between gap-2 mt-3 pt-2.5 border-t border-gray-100">
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-400 min-w-0">
                    <div className="h-5 w-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-medium flex-shrink-0">{initial}</div>
                    <span className="truncate">{uploaderName}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {canManageInvoices && (
                      <button
                        className="h-9 w-9 rounded-lg text-yellow-600 hover:bg-yellow-50 flex items-center justify-center"
                        onClick={() => handleEditInvoice(invoice)}
                        aria-label={el.dashEditTitle}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                      </button>
                    )}
                    <button
                      className="h-9 w-9 rounded-lg text-blue-600 hover:bg-blue-50 flex items-center justify-center"
                      onClick={() => window.open(invoice.url, '_blank')}
                      aria-label={el.dashOpenInvoice}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    </button>
                    {canManageInvoices && (
                      <button
                        className="h-9 w-9 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center"
                        onClick={() => confirmDeleteInvoice(invoice)}
                        aria-label={el.dashDeleteSelected}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop table (>= lg) */}
      {visibleInvoices.length > 0 && (
        <div className="hidden lg:block bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50/80 backdrop-blur sticky top-0 z-10 text-gray-600">
                <tr className="border-b border-gray-200">
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.length > 0 && selectedInvoices.length === visibleInvoices.length}
                      ref={(el) => {
                        if (el) el.indeterminate =
                          selectedInvoices.length > 0 && selectedInvoices.length < visibleInvoices.length;
                      }}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-400"
                      aria-label={el.dashSelectAll}
                    />
                  </th>
                  <SortableHeader
                    label={el.dashTableDate}
                    sortKey="date"
                    sortMode={sortMode}
                    setSortMode={setSortMode}
                    ascValue="date_asc"
                    descValue="date_desc"
                  />
                  <SortableHeader
                    label={el.dashTableIssuer}
                    sortKey="supplier"
                    sortMode={sortMode}
                    setSortMode={setSortMode}
                    ascValue="supplier_asc"
                    descValue="supplier_desc"
                  />
                  <th className="px-4 py-3 text-left font-medium uppercase tracking-wide text-xs">{el.dashTableVat}</th>
                  <SortableHeader
                    label={el.dashTableTotal}
                    sortKey="amount"
                    sortMode={sortMode}
                    setSortMode={setSortMode}
                    ascValue="amount_asc"
                    descValue="amount_desc"
                    align="right"
                  />
                  <th className="px-4 py-3 text-left font-medium uppercase tracking-wide text-xs">{el.dashTableUploadedBy}</th>
                  <th className="px-4 py-3 text-left font-medium uppercase tracking-wide text-xs">{el.dashTableUploadedOn}</th>
                  <th className="px-4 py-3 text-center font-medium uppercase tracking-wide text-xs w-32">{el.dashTableActions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleInvoices.slice(0, displayLimit).map((invoice) => {
                  const isSelected = selectedInvoices.some(inv => inv.id === invoice.id);
                  return (
                    <tr
                      key={invoice.id}
                      className={`transition-colors ${
                        isSelected ? 'bg-blue-50/60 hover:bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectInvoice(invoice)}
                          className="w-4 h-4 rounded text-blue-600 focus:ring-blue-400"
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-800 whitespace-nowrap">{formatHumanDate(invoice.issue_date)}</td>
                      <td className="px-4 py-3 max-w-[260px]">
                        <div className="font-medium text-gray-900 truncate" title={invoice.extracted?.issuer_company_name || ''}>
                          {invoice.extracted?.issuer_company_name || '—'}
                        </div>
                        {invoice.extracted?.invoice_number && (
                          <div className="text-xs text-gray-400 truncate">#{invoice.extracted.invoice_number}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 tabular-nums">{invoice.extracted?.issuer_vat || '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                        {formatCurrency(invoice.extracted?.grand_total)}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const name = invoice.uploadedBy?.name || invoice.uploadedBy?.email || '—';
                          const initial = (invoice.uploadedBy?.name || invoice.uploadedBy?.email || ' ')[0].toUpperCase();
                          return (
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="h-6 w-6 rounded-full bg-blue-600 text-white text-[11px] font-medium flex items-center justify-center flex-shrink-0">{initial}</div>
                              <span className="text-sm text-gray-700 truncate max-w-[160px]" title={name}>{name}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {invoice.uploadedAt ? formatHumanDate(invoice.uploadedAt) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center items-center gap-0.5">
                          {canManageInvoices && (
                            <button
                              className="h-8 w-8 rounded-lg text-yellow-600 hover:bg-yellow-50 flex items-center justify-center"
                              title={el.dashEditTitle}
                              onClick={() => handleEditInvoice(invoice)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                            </button>
                          )}
                          <button
                            className="h-8 w-8 rounded-lg text-blue-600 hover:bg-blue-50 flex items-center justify-center"
                            title={el.dashOpenInvoice}
                            onClick={() => window.open(invoice.url, '_blank')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                          </button>
                          {canManageInvoices && (
                            <button
                              className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center disabled:opacity-40"
                              title={el.dashDeleteSelected}
                              onClick={() => { if (canManageInvoices) confirmDeleteInvoice(invoice); }}
                              disabled={!canManageInvoices}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {visibleInvoices.length > displayLimit && (
        <div className="flex items-center justify-center mt-4 gap-3">
          <span className="text-sm text-gray-500">{el.dashShowingOf(displayLimit, visibleInvoices.length)}</span>
          <button
            onClick={() => setDisplayLimit(prev => prev + 20)}
            className="px-4 py-2 text-sm bg-white border rounded-lg hover:bg-gray-50 text-gray-700"
          >
            {el.dashLoadMore}
          </button>
        </div>
      )}

      {/* Mobile floating bulk action bar */}
      {selectedInvoices.length > 0 && (
        <div className="lg:hidden fixed left-2 right-2 bottom-2 z-40 pb-[env(safe-area-inset-bottom)]">
          <div className="bg-gray-900 text-white rounded-2xl shadow-2xl px-3 py-2.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-7 w-7 rounded-full bg-blue-500 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
                {selectedInvoices.length}
              </span>
              <button
                onClick={() => setSelectedInvoices([])}
                className="text-xs text-gray-300 hover:text-white truncate"
              >
                {el.dashClearSelection}
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={downloadSelectedInvoices}
                className="inline-flex items-center gap-1 px-3 h-9 rounded-lg bg-white/10 hover:bg-white/15 text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M8 12l4 4m0 0l4-4m-4 4V4"/></svg>
                <span className="hidden sm:inline">ZIP</span>
              </button>
              {canManageInvoices && (
                <button
                  onClick={deleteSelectedInvoices}
                  className="inline-flex items-center gap-1 px-3 h-9 rounded-lg bg-red-600 hover:bg-red-500 text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Non-blocking download progress — bottom-left corner */}
      {downloadStatus.active && (
        <div className="fixed bottom-4 left-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm rounded-2xl shadow-2xl px-4 py-3 min-w-[220px] max-w-xs">
          {downloadStatus.error ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          ) : downloadStatus.finished ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
          ) : (
            <svg className="animate-spin h-5 w-5 text-blue-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">
              {downloadStatus.error
                ? el.toastDownloadFailed
                : downloadStatus.finished
                  ? 'Λήψη ολοκληρώθηκε'
                  : `Λήψη αρχείων… ${downloadStatus.done}/${downloadStatus.total}`}
            </div>
            {!downloadStatus.error && !downloadStatus.finished && (
              <div className="mt-1.5 h-1 rounded-full bg-white/20 overflow-hidden">
                <div
                  className="h-full bg-blue-400 rounded-full transition-all duration-300"
                  style={{ width: `${downloadStatus.total ? Math.round((downloadStatus.done / downloadStatus.total) * 100) : 0}%` }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Invoice Dialog — split view */}
      {dialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">{el.dashReviewTitle}</h3>
              <button onClick={handleCancelReview} className="text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body: two columns */}
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
              {/* Left — document preview */}
              <div className="md:w-1/2 bg-gray-100 flex flex-col border-b md:border-b-0 md:border-r overflow-hidden" style={{ minHeight: '200px' }}>
                {filePreview ? (
                  (() => {
                    const isPdf = finalFile?.name?.toLowerCase().endsWith('.pdf') || (finalFile?.type === 'application/pdf');
                    return isPdf ? (
                      <iframe
                        src={filePreview}
                        title="preview"
                        className="w-full h-full flex-1"
                        style={{ minHeight: '300px' }}
                      />
                    ) : (
                      <div className="flex-1 overflow-auto flex items-start justify-center p-2">
                        <img src={filePreview} alt="preview" className="max-w-full object-contain rounded" />
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                )}
                {filePreview && (
                  <div className="px-3 py-2 bg-gray-50 border-t text-center">
                    <a href={filePreview} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                      {el.dashPreviewLink} ↗
                    </a>
                  </div>
                )}
              </div>

              {/* Right — form fields */}
              <div className="md:w-1/2 flex flex-col overflow-hidden">
                {/* Stepper + Scan button */}
                <div className="px-4 sm:px-6 pt-4 pb-3 border-b bg-gradient-to-br from-blue-50/60 to-indigo-50/60">
                  <ExtractionStepper ocr={ocrStep} gpt={gptStep} />
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
                    <p className="text-xs text-gray-500 max-w-sm">{el.dashManualHint}</p>
                    <button
                      onClick={runAiScan}
                      disabled={aiScanning || !extractionUrl}
                      className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-medium shadow-sm hover:shadow-md disabled:opacity-60"
                    >
                      {aiScanning ? (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 11a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                      )}
                      {aiScanning ? el.dashScanning : (gptStep === 'done' || ocrStep === 'error' || gptStep === 'error' ? el.dashScanRetry : el.dashScanWithAi)}
                    </button>
                  </div>
                  {scanError && (
                    <div className="mt-2 text-xs text-red-600">{scanError}</div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                  {['issuer_company_name', 'issuer_vat', 'invoice_number', 'issue_date', 'grand_total'].map((key) => (
                    <div key={key}>
                      <label className="block mb-1 text-sm font-semibold text-gray-700">{formatInvoiceFieldLabel(key)}</label>
                      {key === 'issue_date' ? (
                        <IssueDatePicker
                          value={formData[key]}
                          onChange={(d) => updateReviewField('issue_date', d)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          value={formData[key] || ''}
                          onChange={(e) => updateReviewField(key, e.target.value)}
                        />
                      )}
                      {key === 'issuer_company_name' && formData._nameFromMapping && (
                        <p className="mt-1 text-xs text-emerald-700">{el.dashNameFromMapping}</p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t bg-gray-50">
                  <button
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg border"
                    onClick={handleCancelReview}
                  >
                    {el.dashCancel}
                  </button>
                  <button
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    onClick={handleConfirmSave}
                  >
                    {el.dashSave}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Dialog */}
      {bulkOpen && (
        <Suspense fallback={null}>
          <BulkUploadDialog
            open={bulkOpen}
            onClose={() => setBulkOpen(false)}
            companyId={companyId}
            onSaved={loadInvoices}
          />
        </Suspense>
      )}

      {/* Edit Invoice Dialog — split view */}
      {editInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">{el.dashEditTitle}</h3>
              <button onClick={() => { setEditInvoice(null); setEditFormData({}); }} className="text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body: two columns */}
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
              {/* Left — document preview */}
              <div className="md:w-1/2 bg-gray-100 flex flex-col border-b md:border-b-0 md:border-r overflow-hidden" style={{ minHeight: '200px' }}>
                {editInvoice.url ? (
                  (() => {
                    const isPdf = (editInvoice.fileName || '').toLowerCase().endsWith('.pdf');
                    return isPdf ? (
                      <iframe
                        src={editInvoice.url}
                        title="preview"
                        className="w-full h-full flex-1"
                        style={{ minHeight: '300px' }}
                      />
                    ) : (
                      <div className="flex-1 overflow-auto flex items-start justify-center p-2">
                        <img src={editInvoice.url} alt="preview" className="max-w-full object-contain rounded" />
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                )}
                {editInvoice.url && (
                  <div className="px-3 py-2 bg-gray-50 border-t text-center">
                    <a href={editInvoice.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                      {el.dashPreviewLink} ↗
                    </a>
                  </div>
                )}
              </div>

              {/* Right — form fields */}
              <div className="md:w-1/2 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                  {['issuer_company_name', 'issuer_vat', 'invoice_number', 'issue_date', 'grand_total'].map((key) => (
                    <div key={key}>
                      <label className="block mb-1 text-sm font-semibold text-gray-700">{formatInvoiceFieldLabel(key)}</label>
                      {key === 'issue_date' ? (
                        <IssueDatePicker
                          value={editFormData[key]}
                          onChange={(d) => setEditFormData({ ...editFormData, [key]: d })}
                        />
                      ) : (
                        <input
                          type="text"
                          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          value={editFormData[key] || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, [key]: e.target.value })}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
                  <button
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg border"
                    onClick={() => { setEditInvoice(null); setEditFormData({}); }}
                  >
                    {el.dashCancel}
                  </button>
                  <button
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    onClick={handleSaveEdit}
                  >
                    {el.dashSave}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteInvoiceDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">{el.dashConfirmDeleteTitle}</h3>
            <div className="flex items-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-yellow-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>
                {el.dashDeleteIntro}{' '}
                <span className="font-semibold">{invoiceToDelete?.fileName}</span>
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <button 
                className="px-4 py-2 border rounded hover:bg-gray-100" 
                onClick={() => setDeleteInvoiceDialog(false)}
              >
                {el.dashConfirmDeleteNo}
              </button>
              <button 
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                onClick={deleteInvoice}
              >
                {el.dashConfirmDeleteYes}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Review Confirmation Dialog */}
      {cancelConfirmVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">{el.dashDiscardTitle}</h3>
            <div className="flex items-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-yellow-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>
                {el.dashDiscardBody}
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <button 
                className="px-4 py-2 border rounded hover:bg-gray-100" 
                onClick={() => setCancelConfirmVisible(false)}
              >
                {el.dashDiscardBack}
              </button>
              <button 
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                onClick={confirmCancelReview}
              >
                {el.dashDiscardConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Scanner */}
      <CameraScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onComplete={async (images) => {
          const valid = images.filter(f => f && f.size > 0);
          const pdf = await convertImagesToPdf(valid.length ? valid : images);
          await processFile(pdf);
        }}
      />
    </div>
  );
}